import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface MonthStripProps {
  /** Labels in order, e.g. ["Jan", "Feb", …]. */
  options: { value: string; label: string; disabled?: boolean }[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  "aria-label"?: string;
}

/** Horizontal pill strip with previous/next arrows: the black pill is the selection. */
export default function MonthStrip({ options, value, onChange, className, ...props }: MonthStripProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const index = Math.max(0, options.findIndex((o) => o.value === value));

  useEffect(() => {
    const el = scroller.current?.querySelector<HTMLElement>("[aria-pressed=true]");
    el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [value]);

  const step = (dir: -1 | 1) => {
    let i = index + dir;
    while (options[i]?.disabled) i += dir;
    if (options[i]) onChange(options[i].value);
  };

  const arrow =
    "flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-body transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className={cn("flex min-w-0 items-center gap-1.5", className)} role="group" aria-label={props["aria-label"]}>
      <button type="button" className={arrow} onClick={() => step(-1)} disabled={index <= 0} aria-label="Previous">
        <ChevronLeft className="size-4" />
      </button>
      <div
        ref={scroller}
        className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={active}
              disabled={o.disabled}
              onClick={() => onChange(o.value)}
              className={cn(
                "h-9 min-w-[72px] shrink-0 rounded-full px-4 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40",
                active
                  ? "bg-primary font-medium text-primary-foreground shadow-soft"
                  : "bg-muted text-body hover:bg-accent hover:text-foreground",
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className={arrow}
        onClick={() => step(1)}
        disabled={index >= options.length - 1}
        aria-label="Next"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}
